import React from "react";
import {
  Plus,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Ban,
  Trash2,
  Copy,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { TimeRangeInput } from "./TimeRangeInput";
import { TimeRangeSlotDto } from "../../../web-api-client";
import { format } from "date-fns/format";
import { ConfirmDeleteMenuItem } from "../../ui/ConfirmDeleteMenuItem";

export function TimeSlotCell({
  slot,
  children,
  maxVisible = 3,
  onAddDoctor,
  onDelete,
  onUpdateTimeRange,
  onChangeTimeRangeStatus,
  onCopyToDate,
}: {
  slot: TimeRangeSlotDto;
  children: React.ReactNode;
  maxVisible?: number;
  onAddDoctor?: () => void;
  onDelete: (slot: TimeRangeSlotDto) => void;
  onUpdateTimeRange: (next: {
    start: string;
    end: string;
  }) => Promise<number | undefined>;
  onChangeTimeRangeStatus: (
    timeRange: TimeRangeSlotDto,
    status: "O" | "C",
  ) => Promise<void>;
  onCopyToDate?: (slot: TimeRangeSlotDto) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);

  const childArray = React.Children.toArray(children);
  const total = childArray.length;
  const hiddenCount = Math.max(total - maxVisible, 0);

  const shown = expanded ? childArray : childArray.slice(0, maxVisible);

  const formatTime = (date: Date | undefined) => {
    if (!date) return "";
    return format(date, "HH:mm");
  };

  const startTime = formatTime(slot.startTime);
  const endTime = formatTime(slot.endTime);

  return (
    <div
      className={`relative border border-primary/40 rounded-lg shadow-sm mt-2 bg-white`}
      onClick={(e: any) => {
        e.stopPropagation();
      }}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${slot.status === "C" ? "bg-gray-500/40" : "bg-blue-500/40 "}`}
      />

      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center text-sm font-bold text-gray-800">
            <img src="/icons/clock.png" className="w-5 h-5" alt="Clock" />
            {/* <span>{timeRange}</span> */}
            <TimeRangeInput
              start={startTime}
              end={endTime}
              disabled={false}
              onCommit={onUpdateTimeRange}
              locked={slot.status === "C"}
            />
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 absolute top-1 right-1 "
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onCopyToDate?.(slot);
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy sang ngày khác
                </DropdownMenuItem>

                {slot.status !== "C" ? (
                  <DropdownMenuItem
                    onClick={(e: any) => {
                      e.stopPropagation();
                      onChangeTimeRangeStatus?.(slot, "C");
                    }}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Đóng khung giờ
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={(e: any) => {
                      e.stopPropagation();
                      onChangeTimeRangeStatus?.(slot, "O");
                    }}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Mở khung giờ
                  </DropdownMenuItem>
                )}

                <ConfirmDeleteMenuItem
                  title="Xóa khung giờ?"
                  description="Thao tác này sẽ xóa toàn bộ bác sĩ trong khung giờ."
                  onConfirm={() => onDelete(slot)}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {total > maxVisible && (
          <button
            type="button"
            className="text-xs font-semibold text-primary underline hover:underline inline-flex items-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? (
              <span className="flex">
                Thu gọn <ChevronUp className="h-3.5 w-3.5" />
              </span>
            ) : (
              <span className="flex">
                +{hiddenCount} bác sĩ <ChevronDown className="h-3.5 w-3.5" />
              </span>
            )}
          </button>
        )}
      </div>

      <div className="px-3 pb-2">
        <div
          className={expanded ? "flex flex-col gap-2" : "flex flex-col gap-2"}
        >
          {shown}
        </div>
      </div>

      <div
        className={`px-3 pb-3 pt-2 border-t border-gray-100 rounded-b-lg bg-white`}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddDoctor?.();
          }}
          className="w-full py-2 text-xs text-green-800 bg-green-50 font-semibold rounded-md border border-gray-200 hover:border-primary flex items-center justify-center gap-1 transition-all"
        >
          <Plus className="w-3 h-3" />
          <span>Thêm bác sĩ</span>
        </button>
      </div>
    </div>
  );
}
